'use client';

import { c as _c } from "react/compiler-runtime";
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import { Button } from '@uzh-bf/design-system';
import { useTranslations } from 'next-intl';
import RichTextHtml from '~/components/RichTextHtml';
import { Media } from '~/payload-types';
import { getFileIcon, getVersionedAttachmentUrl } from './attachmentUtils';
type LexicalNode = {
  type?: string;
  fields?: {
    url?: string;
    [key: string]: unknown;
  };
  children?: LexicalNode[];
  [key: string]: unknown;
};
function processLexicalNode(node: LexicalNode, attachmentsByFilename: Map<string, Media>): LexicalNode {
  // Handle link nodes
  if (node.type === 'link' && node.fields?.url) {
    const url = node.fields.url;
    // Check if this link points to an attachment filename
    const attachment = attachmentsByFilename.get(url);
    if (attachment) {
      const properUrl = getVersionedAttachmentUrl(attachment);
      node = {
        ...node,
        fields: {
          ...node.fields,
          url: properUrl || url
        }
      };
    }
  }

  // Recursively process children
  if (node.children && Array.isArray(node.children)) {
    node = {
      ...node,
      children: node.children.map(child => processLexicalNode(child, attachmentsByFilename))
    };
  }
  return node;
}
function TextBlock(t0) {
  const $ = _c(17);
  const {
    content,
    attachments
  } = t0;
  const t = useTranslations();
  let t1;
  let t2;
  let t3;
  if ($[0] !== attachments || $[1] !== content) {
    const attachmentsByFilename = new Map();
    attachments?.forEach(attachment => {
      if (attachment.filename) {
        attachmentsByFilename.set(attachment.filename, attachment);
      }
    });
    t3 = content;
    t2 = content.root;
    t1 = content.root.children.map(child => processLexicalNode(child as LexicalNode, attachmentsByFilename));
    $[0] = attachments;
    $[1] = content;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = t1 as SerializedEditorState['root']['children'];
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = {
      ...t2,
      children: t4
    };
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = {
      ...t3,
      root: t5
    };
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const processedContent = t6;
  let t7;
  if ($[11] !== attachments || $[12] !== processedContent || $[13] !== t) {
    const preparedAttachments = attachments?.flatMap(_temp) ?? [];
    let t8;
    if ($[15] !== processedContent) {
      t8 = <RichTextHtml data={processedContent} />;
      $[15] = processedContent;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = <div>{t8}{preparedAttachments.length > 0 && <section className="mt-5"><div className="mb-2 flex items-center gap-3"><h3 className="text-xs font-semibold tracking-wide text-gray-600 uppercase">{t("unit.attachmentsHeading")}</h3><div className="h-px flex-1 bg-gray-200" /></div><div className="flex flex-row flex-wrap gap-1">{preparedAttachments.map(_temp2)}</div></section>}</div>;
    $[11] = attachments;
    $[12] = processedContent;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp2(attachment_1) {
  return <Button key={attachment_1.id} size="sm" title={attachment_1.displayName} className={{
    root: "max-w-full gap-1"
  }} onClick={() => {
    const link = document.createElement("a");
    link.href = attachment_1.url;
    link.rel = "noopener noreferrer";
    if (attachment_1.downloadFilename) {
      link.download = attachment_1.downloadFilename;
    }
    document.body.appendChild(link);
    link.click();
    link.remove();
  }}>{attachment_1.fileIcon}<span className="block max-w-[16rem] truncate">{attachment_1.displayName}</span></Button>;
}
function _temp(attachment_0) {
  const url = getVersionedAttachmentUrl(attachment_0);
  if (!url) {
    return [];
  }
  return [{
    id: attachment_0.id,
    url,
    fileIcon: getFileIcon(attachment_0.filename ?? undefined),
    displayName: attachment_0.filename ?? "Download",
    downloadFilename: attachment_0.filename ?? undefined
  }];
}
export default TextBlock;